import React, { useEffect, useMemo, useState } from "react";
import { resolveVideoSource } from "./resolveSource";
import WebRTCPlayer from "./providers/WebRTCPlayer";
import HlsPlayer from "./providers/HlsPlayer";
import IframePlayer from "./providers/IframePlayer";

// Provider-agnostic live-video surface for PRYMEBET live blackjack.
//
// Takes a table's video config, resolves a primary + optional fallback source,
// and renders the matching provider. If the primary transport exhausts its
// retries it transparently switches to the fallback (surfacing the status),
// so the blackjack UI never has to know how any provider works.

function renderProvider(source, { health, setHealth, onExhausted, tierKey, muted }) {
  if (!source) return null;
  const key = `${tierKey}:${source.src}`;
  const common = {
    src: source.src,
    onHealth: setHealth,
    onExhausted,
    muted,
  };
  if (source.kind === "webrtc") return <WebRTCPlayer key={key} {...common} />;
  if (source.kind === "hls") return <HlsPlayer key={key} {...common} />;
  return <IframePlayer key={key} src={source.src} onHealth={setHealth} />;
}

// chrome=false hides the built-in status pill / gradient / label so an immersive
// caller (the live-blackjack dealer table) can own the overlays; onHealth surfaces
// the transport health ("live"|"connecting"|"reconnecting"|"offline") to that caller.
// muted (default true, required for autoplay) is passed through to the <video> providers.
export default function LiveVideoPlayer({ config, className = "", chrome = true, onHealth, muted = true }) {
  const { primary, fallback } = useMemo(() => resolveVideoSource(config), [config]);

  // Which tier is active. Reset whenever the resolved sources change.
  const [tier, setTier] = useState("primary");
  const [health, setHealth] = useState("offline");

  useEffect(() => {
    setTier("primary");
    setHealth(primary || fallback ? "connecting" : "offline");
  }, [primary?.src, fallback?.src]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { onHealth?.(health); }, [health, onHealth]);

  const active = tier === "primary" ? (primary || fallback) : fallback;
  const usingFallback = tier === "fallback" && !!fallback;

  const onExhausted = () => {
    // Primary gave up: fall back if we have one, otherwise stay offline.
    if (tier === "primary" && fallback) {
      setTier("fallback");
      setHealth("connecting");
    } else {
      setHealth("offline");
    }
  };

  return (
    <div className={`absolute inset-0 overflow-hidden bg-black ${className}`} data-testid="bjv2-live-video">
      {active
        ? renderProvider(active, { health, setHealth, onExhausted, tierKey: tier, muted })
        : (
          <div className="absolute inset-0 flex items-center justify-center bg-[radial-gradient(circle_at_50%_35%,#232329_0%,#09090b_55%,#000_100%)]">
            <div className="text-center">
              <p className="text-[10px] font-black uppercase tracking-[0.35em] text-white/35">PRYMEBET LIVE</p>
              <p className="mt-2 text-sm font-bold text-white/60">Dealer video not configured</p>
            </div>
          </div>
        )}

      {chrome && (
        <>
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/35 via-transparent to-black/70" />

          <div className="absolute left-3 top-3 z-20 flex items-center gap-2 rounded-full bg-black/65 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-white backdrop-blur">
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                health === "live"
                  ? "animate-pulse bg-[#EF4444]"
                  : health === "connecting" || health === "reconnecting"
                    ? "bg-amber-400"
                    : "bg-zinc-500"
              }`}
            />
            {health === "live"
              ? "Live dealer"
              : health === "reconnecting"
                ? "Reconnecting"
                : health === "connecting"
                  ? "Connecting"
                  : "Video offline"}
          </div>

          {active?.label && (
            <span className="absolute right-3 top-3 z-20 flex items-center gap-1 rounded-full bg-black/55 px-2 py-1 text-[9px] font-bold uppercase tracking-wide text-white/70 backdrop-blur">
              {active.label}
              {usingFallback && <span className="text-amber-300">· fallback</span>}
            </span>
          )}
        </>
      )}
    </div>
  );
}
